import type { FilterRuleModel } from '@/shared/generated'
import type {
	OptimizationGroupsType,
	SelectedOptimizationGroupType,
} from '../lib/helpers'

import React, { useEffect, useMemo, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { map, prop } from 'lodash/fp'
import { useTranslation } from 'react-i18next'

import { CurrencyPicker } from '@/features/currency-picker'

import { CurrencyScopeProvider, useCurrenciesQuery } from '@/entities/currency'
import { useOptimizationGroupsByProductFiltersQuery } from '@/entities/optimization-statistic'

import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import { Label } from '@/shared/components/ui/label'
import { Content, Header, Layout } from '@/shared/components/ui/layout'
import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
} from '@/shared/components/ui/sheet'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { Switch } from '@/shared/components/ui/switch'
import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from '@/shared/components/ui/tabs'
import { Tag } from '@/shared/components/ui/tag'
import { OptimizationStatus } from '@/shared/generated'
import { useLocationQuery } from '@/shared/hooks/useLocationQuery'
import { ampli } from '@/shared/lib/ampli'
import { cn } from '@/shared/lib/utils'
import { formatNumber, NumberFormats } from '@/shared/tools/locale'

import { MetricsSummaryWidget } from './MetricsSummaryWidget'
import { OpenStateProvider } from './OpenStateProvider'
import { OptimizationGroupsDropdown } from './OptimizationGroupsDropdown'
import { OptimizationTab } from './OptimizationTab'
import { ProgressTab } from './ProgressGlobalTab'
import { StatisticsTab } from './StatisticsTab'

const emptyArray: [] = []
const OptimizationsSummary = ({
	filters: queryFilters,
}: {
	filters: FilterRuleModel[]
}) => {
	const { t } = useTranslation()

	const [shouldUseQueryFilters, setShouldUseQueryFilters] = useState(false)
	const [selectedOptimizationGroups, setSelectedOptimizationGroups] = useState<
		OptimizationGroupsType[] | null
	>(null)
	const filters = useMemo(
		() => (shouldUseQueryFilters ? queryFilters : emptyArray),
		[shouldUseQueryFilters, queryFilters]
	)
	const { data: optimizationGroupsByFilters, isLoading: isGroupsLoading } =
		useOptimizationGroupsByProductFiltersQuery({ filters })
	// Hold widgets until the currency is known to avoid a double fetch.
	const { isPending: isCurrenciesPending } = useCurrenciesQuery()
	const isLoading = isGroupsLoading || isCurrenciesPending
	useEffect(() => {
		const optimizationGroupsByProductFilters =
			optimizationGroupsByFilters?.filter(
				({ status }: OptimizationGroupsType) =>
					status === OptimizationStatus.Finished ||
					status === OptimizationStatus.Locked
			)

		return setSelectedOptimizationGroups(optimizationGroupsByProductFilters!)
	}, [optimizationGroupsByFilters])
	const { finishedOgsSummary, queryParams } = useMemo(
		() => ({
			finishedOgsSummary: optimizationGroupsByFilters && {
				total: optimizationGroupsByFilters?.length,
				totolFinished: selectedOptimizationGroups?.length || 0,
				totalLocked:
					selectedOptimizationGroups?.filter(
						({ status }) => status === OptimizationStatus.Locked
					).length || 0,
			},
			queryParams: selectedOptimizationGroups
				? {
						optimizations: buildOptimizationsQueryObject(
							selectedOptimizationGroups
						),
						filters: filters?.filter(
							({ name }) => name !== 'optimization_group_id'
						),
					}
				: null,
		}),
		[selectedOptimizationGroups]
	)
	const handleApply = (selectedOgs: SelectedOptimizationGroupType[]) => {
		const optimizationGroups: OptimizationGroupsType[] =
			selectedOgs.map(
				({ id }) =>
					optimizationGroupsByFilters?.find(
						(data) => data.id === id
					) as OptimizationGroupsType
			) || []

		return setSelectedOptimizationGroups(optimizationGroups)
	}

	return (
		<Layout className='h-full'>
			<SheetHeader>
				<SheetTitle className='text-base'>{t('opt.summary')}</SheetTitle>
			</SheetHeader>
			<Header className='flex flex-col space-y-3 px-4 py-3'>
				<MetricsSummaryWidget isLoading={isLoading} queryParams={queryParams} />
				{isLoading && (
					<div className='flex gap-2'>
						<Skeleton className='h-6 w-20' />
						<Skeleton className='h-6 w-20' />
					</div>
				)}
				{finishedOgsSummary && !isLoading && (
					<div className='flex items-center gap-2'>
						<Tag variant='success'>
							{t('og.status.finished.badge', { defaultValue: 'Finished OGs' })}:{' '}
							{formatNumber(
								(finishedOgsSummary.totolFinished / finishedOgsSummary.total) *
									100 || 0,
								NumberFormats.Percent
							) || 0}
						</Tag>
						<Tag variant='info'>
							{t('og.status.applied.badge', { defaultValue: 'Applied OGs' })}:{' '}
							{formatNumber(
								(finishedOgsSummary.totalLocked / finishedOgsSummary.total) *
									100 || 0,
								NumberFormats.Percent
							) || 0}
						</Tag>
					</div>
				)}
				{isLoading ? (
					<div className='flex justify-between'>
						<Skeleton className='h-8 w-32' />
						<Skeleton className='h-8 w-32' />
					</div>
				) : (
					<div className='flex items-center justify-between'>
						<div className='flex items-center'>
							<Switch
								checked={shouldUseQueryFilters}
								onCheckedChange={(checked) => {
									ampli.optSummaryGlobUseTableFiltersSwitch({
										value: String(checked),
									})
									setShouldUseQueryFilters(checked)
								}}
							/>
							<Label className='mx-2 flex items-center font-normal'>
								{t('opt.summary.use_table_filters', {
									defaultValue: 'Use table filter',
								})}
							</Label>
						</div>
						<div className='flex items-center gap-2'>
							<CurrencyPicker />
							<OptimizationGroupsDropdown
								optimizationGroups={
									getFinishedOpimizationGroups(
										optimizationGroupsByFilters || []
									) || []
								}
								isDisabled={isLoading || shouldUseQueryFilters}
								selectedOgs={selectedOptimizationGroups}
								handleClear={() => setSelectedOptimizationGroups([])}
								handleApply={handleApply}
							/>
						</div>
					</div>
				)}
			</Header>
			<Tabs defaultValue='statistics' className='overflow-hidden'>
				<TabsList className='mx-4'>
					<TabsTrigger value='statistics' data-tab='statistics'>
						{t('app.statistics', { defaultValue: 'Statistics' })}
					</TabsTrigger>
					<TabsTrigger value='optimization' data-tab='optimization'>
						{t('app.optimization', { defaultValue: 'Optimization' })}
					</TabsTrigger>
					<TabsTrigger value='progress' data-tab='progress'>
						{t('app.progress', { defaultValue: 'Progress' })}
					</TabsTrigger>
				</TabsList>
				<Content className='relative overflow-y-auto'>
					<AnimatePresence mode='wait'>
						<TabsContent value='statistics' asChild>
							<TabContent key='statistics'>
								<StatisticsTab
									isLoading={isLoading}
									queryParams={queryParams}
								/>
							</TabContent>
						</TabsContent>
						<TabsContent value='optimization' asChild>
							<TabContent key='optimization'>
								<OptimizationTab
									queryParams={queryParams}
									isLoading={isLoading}
								/>
							</TabContent>
						</TabsContent>
						<TabsContent value='progress' asChild>
							<TabContent key='progress'>
								<ProgressTab
									isLoading={isLoading}
									productFilters={filters}
									optimizationGroups={getSelectedOGs(
										selectedOptimizationGroups,
										optimizationGroupsByFilters || []
									)}
								/>
							</TabContent>
						</TabsContent>
					</AnimatePresence>
				</Content>
			</Tabs>
		</Layout>
	)
}

const getSelectedOGs = (
	selectedOptimizationGroups: SelectedOptimizationGroupType[] | null,
	optimizationsGroups: OptimizationGroupsType[]
) => {
	const ogsIds = map(prop('id'), selectedOptimizationGroups)

	return optimizationsGroups?.filter(({ id }) => ogsIds.includes(id))
}

export function OptimizationsSummaryDrawer({
	isOpen,
	close,
}: {
	isOpen: boolean
	close: () => void
}) {
	const [{ filters }] = useLocationQuery()

	return (
		<Sheet
			modal={false}
			defaultOpen={isOpen}
			open={isOpen}
			onOpenChange={(open) => !open && close()}
		>
			<SheetContent
				onPointerDownOutside={(e) => e.preventDefault()}
				onInteractOutside={(e) => e.preventDefault()}
				disbleOverlay
				className='inset-y-2 right-2 h-auto w-[600px] gap-0 rounded-3xl border bg-background/80 backdrop-blur-xl sm:max-w-[600px]'
			>
				<ErrorBoundary>
					<OpenStateProvider>
						<CurrencyScopeProvider>
							<OptimizationsSummary filters={filters} />
						</CurrencyScopeProvider>
					</OpenStateProvider>
				</ErrorBoundary>
			</SheetContent>
		</Sheet>
	)
}
const buildOptimizationsQueryObject = (
	optimizationGroups: OptimizationGroupsType[]
) =>
	optimizationGroups?.map(({ id, optimization }) => ({
		optimization_id: optimization,
		optimization_group_id: id,
	}))
export const TabContent = ({
	children,
	className,
}: {
	children: React.ReactNode
	className?: string
}) => (
	<motion.div
		initial={{ opacity: 0, x: 100 }}
		animate={{ opacity: 1, x: 0 }}
		exit={{ opacity: 0, x: -30 }}
		className={cn(
			'divide-base flex size-full flex-col divide-y divide-solid',
			className
		)}
		transition={{ duration: 0.2 }}
	>
		{children}
	</motion.div>
)
const getFinishedOpimizationGroups = (
	optimizationGroups: OptimizationGroupsType[]
) =>
	optimizationGroups?.filter(
		({ status }) =>
			status === OptimizationStatus.Finished ||
			status === OptimizationStatus.Locked
	) || []
